/**
 * @file Controller for the dialog that shows up on interactions
 * @author Gonzalo Alvarez de Aldecoa <gon@galdecoa.com>
 * @license GPL-3.0-or-later
 * @version 2304.11.001
 */

/** @module gameinteractivesmodal */

import { handleExternalLinks } from "./externalLinksHandler.js";

class GameInteractivesModal {

	/** 
	 * The `dialog` element to be used as modal pop-up.
	 * @type {HTMLDialogElement?}
	 */
	#dialog = null;

	/**
	 * The `{@link module:gameinteractivemodal~dialog}` header title.
	 * @type {HTMLHeadingElement?}
	 */
	#dialogTitle = null;

	/**
	 * The content `section` element in `{@link module:gameinteractivemodal~dialog}`.
	 * @type {HTMLSectionElement?}
	 */
	#dialogContent = null;

	/** 
	 * The `Game` instance.
	 * @type {module:game~Game?}
	 */
	#game = null;

	/** 
	 * The `canvas` element the game is rendered in.
	 * @type {HTMLCanvasElement?}
	 */
	#canvas = null;

	keepGamePausedOnClose = false;

	/**
	 * A method that will be called
	 * @param {*} [content]
	 */
	filterLoadedContent = function( content = this.#dialogContent ) { }

	/**
	 * Load `html` content into `{@link module:gameinteractivemodal~dialog}`
	 * @param {String} html 
	 */
	show( html ) {

		this.#game.pause();

		this.#dialogContent.innerHTML = html;

		const contentHeader = this.#dialogContent.querySelector( "header" );
		const contentTitle = this.#dialogContent.querySelector( ".title" );
		this.#dialogTitle.innerHTML = contentTitle.innerHTML;
		contentTitle.parentElement.removeChild( contentTitle );
		if ( contentHeader && contentHeader.parentElement ) { 
			contentHeader.parentElement.removeChild( contentHeader ); 
		}

		const openDetails = this.#dialogContent.querySelectorAll( "details[open]" );
		for( let d = openDetails.length - 1; d >= 0; d = d - 1 ) {
			openDetails[ d ].removeAttribute( "open" );
		}

		handleExternalLinks( this.#dialogContent );
		this.filterLoadedContent( this.#dialogContent );

		this.#dialog.showModal();

		this.#dialog.scrollTop = 0;

		document.documentElement.classList.add( "has-open-modal" );

	}

	#getExperienceEmployerElements( contentElement ) {
		let employerElements = contentElement.querySelectorAll(".employer");
		if ( employerElements.length == 2 ) { return employerElements; }
		let worksFor = contentElement.parentElement;
		while ( worksFor && ( worksFor.getAttribute( "itemprop" ) !== "worksFor" ) ) {
			worksFor = worksFor.parentElement;
		}
		if ( !worksFor ) { return; }
		return worksFor.querySelectorAll( ".employer" );
	}


	#onGameActionButtonClick( clickEvent ) {
		this.#game.fireInteractionEvent();
	}

	#showExperienceWithClientInfo( contentSelector )  {

		const contentElement = document.querySelector( contentSelector );
		let clientElement = contentElement;
		while( clientElement && !clientElement.classList.contains( "client" ) ) {
			clientElement = clientElement.parentElement;
		}
		if ( !clientElement ) {
			// TODO: Handle error
		}

		const contentElementClone = contentElement.cloneNode( true );
		
		// Get header
		let contentElementCloneHeader = contentElementClone.querySelector( "header" );
		// If not present, copy it from the client element
		if ( !contentElementCloneHeader ) {
			contentElementCloneHeader = clientElement
				.querySelector( "header" )
				.cloneNode( true )
			;
			contentElementClone.prepend( contentElementCloneHeader );
		}

		// Add client logo to title
		const title = contentElementCloneHeader.classList.contains( "title" )
			? contentElementCloneHeader
			: contentElementCloneHeader.querySelector( ".title" )
		;
		title.innerHTML = ""
			+ "<span class=\"name\">" + title.innerHTML + "</span>"
			+ "<span class=\"logos\"></span>"
		;
		const logos = title.querySelector( ".logos" );
		const clientLogos = contentElementCloneHeader.querySelectorAll( ".logo" );
		for( let l = 0, length = clientLogos.length; l < length; l = l + 1 ) {
			logos.appendChild( clientLogos[ l ] );
		}
		
		
		const divHeader = contentElementClone.matches("div.header")
			? contentElementClone
			: contentElementClone.querySelector( "div.header" )
		;
		if ( divHeader ) {
			// Add employer, if needed
			let employerElements = contentElementClone.querySelectorAll( 
				".employer" 
			);
			if ( employerElements.length !== 2 ) {
				let worksForElement = clientElement;
				while( 
					   worksForElement 
					&& ( worksForElement.getAttribute( "itemprop" ) !== "worksFor" )
				) {
					worksForElement = worksForElement.parentElement;
				}
				employerElements = this.#getExperienceEmployerElements( 
					worksForElement.cloneNode( true ) 
				);
			}
			for( let e = 0, l = employerElements.length; e < l; e = e + 1 ) {
				divHeader.appendChild( employerElements[ e ] );
			}
		}

		this.#game.pause();
		this.#dialog.classList.add( clientElement.id );
		this.show( contentElementClone.innerHTML );

	}

	showExperienceWithClientInfo( contentSelector ) {
		return this.#showExperienceWithClientInfo( contentSelector );
	}

	#onCanvasInteraction( interactionEvent ) {

		if ( 
			   interactionEvent.detail.hasOwnProperty( "goTo" )
			&& (
				   interactionEvent.detail.goTo.hasOwnProperty( "screen" )
				|| interactionEvent.detail.goTo.hasOwnProperty( "position" )
			)
		) {
			this.#game.goToScreen( 
				interactionEvent.detail.goTo.screen,
				interactionEvent.detail.goTo.position 
			);
			return;
		}

		const contentSelector = interactionEvent.detail.name;
		const contentElement = document.querySelector( contentSelector );
		let contentHTML = contentElement
			? contentElement.innerHTML
			: "<h3>Oops</h3><p>No content found</p>"
		;

		const dialogClassNames = [ interactionEvent.detail.type ];

		if ( interactionEvent.detail.type == "technical-skills" ) {
			dialogClassNames.push( contentElement.id );
		}

		this.#dialog.classList.add( ...dialogClassNames );

		if ( interactionEvent.detail.type == "experience" ) {
			this.#showExperienceWithClientInfo( interactionEvent.detail.name );
			return;
		}

		this.#game.pause();
		this.show( contentHTML );

	}

	#onDialogClose( closeEvent ) { 
		document.documentElement.classList.remove( "has-open-modal" );
		this.#dialog.className = "modal";
		this.#dialogTitle.innerHTML = "";
		this.#dialogContent.innerHTML = "";
		this.#game.pause( this.keepGamePausedOnClose );
	}

	#addEventListeners() {
		const onCanvasInteraction = this.#onCanvasInteraction.bind( this );
		this.#canvas.addEventListener( "interaction", onCanvasInteraction );
		const onDialogClose = this.#onDialogClose.bind( this );
		this.#dialog.addEventListener( "close", onDialogClose );
	}

	close() { this.#dialog.close(); }

	constructor( game, dialog ) {

		this.#game = game;
		this.#canvas = game.canvas;
		this.#dialog = dialog;
		this.#dialogTitle = this.#dialog.querySelector( "header .title" );
		this.#dialogContent = this.#dialog.querySelector( "section" );

		this.#addEventListeners();

	}

}

export { GameInteractivesModal }